/**
 * FikoOpenCascadeBridge - OpenCascade.js Worker Bridge
 *
 * OpenCascade.js 1.1.1 worker'ı ile iletişim köprüsü.
 * CAF/XDE desteği ile renk, tessellation ve topoloji bilgisi alır.
 *
 * @author FikoYT
 */

/**
 * OpenCascade.js'den gelen renk bilgisi
 */
export interface OCCTColor {
    r: number;
    g: number;
    b: number;
    a?: number;
}

/**
 * OpenCascade Interface_Static okuma parametreleri
 */
export interface OpenCascadeReadParams {
    /** Surface curve modu (0: default, 3: 3D preferred) */
    surfaceCurveMode?: number;
    /** Precision modu (0: file, 1: user) */
    precisionMode?: number;
    /** Precision değeri (mm) */
    precisionVal?: number;
    /** Maksimum precision değeri (mm) */
    maxPrecisionVal?: number;
    /** Product modu ("0" veya "1") */
    productMode?: string;
    /** Assembly level (0, 1, 2) */
    assemblyLevel?: number;

    // Tessellation parametreleri
    /** Multi-threaded tessellation (true/false) */
    inParallel?: boolean;
    /** Minimum triangle boyutu (mm) */
    minSize?: number;
    /** İç vertex'leri kullan */
    internalVerticesMode?: boolean;
    /** Yüzey sapma kontrolü */
    controlSurfaceDeflection?: boolean;

    // Encoding & Regularity
    /** Açı birimi kontrolü (varsayılan 0.01) */
    encodeRegularityAngle?: number;

    // Step Schema & Version
    /** STEP şeması (AP203, AP214, AP242) */
    stepSchema?: string;
    /** Shape processing sıralaması */
    stepSequence?: string;
    /** Resource dosyası */
    resourceName?: string;

    // Advanced Reading
    /** Non-manifold okuma (0/1) */
    nonManifold?: number;
    /** IDEAS format uyumluluğu (0/1) */
    ideasMode?: number;
    /** Tüm shape'leri oku (true/false) */
    allShapes?: boolean;

    // Unit
    /** Birim dönüşümü (MM/CM/M/INCH/FT) */
    unit?: string;

    /** Shape healing / kapama onarımı */
    shapeHealing?: boolean;
    /** Ayrı solid'leri tek body altında birleştir */
    mergeBodies?: boolean;
    /** Kısa edge tolerans değeri (mm) */
    shortEdgeTolerance?: number;
}

/**
 * Tessellation parametreleri - BRepMesh_IncrementalMesh / IMeshTools_Parameters
 *
 * OpenCascade mesh oluşturma kalite ayarları.
 * occt-import-js kütüphanesi ile uyumlu parametreler.
 *
 * Kalite Seviyeleri (önerilen):
 * - Ultra Hızlı: linear=1.0mm, angular=60° (büyük dosyalar, preview)
 * - Hızlı:       linear=0.5mm, angular=45° (orta kalite)
 * - Normal:      linear=0.1mm, angular=30° (standart)
 * - Yüksek:      linear=0.05mm, angular=15° (detaylı)
 * - Çok Yüksek:  linear=0.01mm, angular=5° (maksimum detay)
 *
 * @see https://github.com/kovacsv/occt-import-js
 * @see https://old.opencascade.com/doc/occt-7.5.0/overview/html/occt_user_guides__mesh.html
 */
export interface TessellationParams {
    /**
     * Lineer sapma (mm cinsinden)
     * Mesh noktaları ile gerçek yüzey arasındaki maksimum mesafe.
     * Düşük değer = Daha kaliteli mesh, daha fazla triangle.
     *
     * @default 0.1 (mm)
     * @range 0.001 - 10.0
     */
    linearDeflection?: number;

    /**
     * Açısal sapma (radyan cinsinden)
     * Ardışık mesh segmentleri arasındaki maksimum açı.
     * Eğri yüzeylerde pürüzsüzlüğü kontrol eder.
     *
     * Dönüşüm: derece → radyan = derece * (π / 180)
     * Örnekler: 30° = 0.524 rad, 15° = 0.262 rad, 5° = 0.087 rad
     *
     * @default 0.5 (radyan ≈ 28.6°)
     * @range 0.01 - 1.5
     */
    angularDeflection?: number;

    /**
     * Göreceli mod
     * true: deflection değerleri bounding box'a göreceli oran olarak yorumlanır
     * false: deflection değerleri mutlak mm cinsinden
     *
     * occt-import-js'de "linearDeflectionType: bounding_box_ratio" karşılığı
     *
     * @default false
     */
    relativeMode?: boolean;

    /** OpenCascade Interface_Static okuma parametreleri */
    readParams?: OpenCascadeReadParams;
}

/**
 * Tek bir mesh'in verileri
 */
export interface OCCTMeshData {
    /** Vertex pozisyonları (x, y, z, x, y, z, ...) */
    vertices: Float32Array;
    /** Triangle indisleri */
    indices: Uint32Array;
    /** Normal vektörleri */
    normals: Float32Array;
    /** Face indeksi (hangi yüzeye ait) */
    faceIndex: number;
    /** Yüzey rengi */
    color: OCCTColor | null;
}

/**
 * Face (yüzey) topoloji bilgisi
 */
export interface OCCTFaceInfo {
    id: number;
    surfaceType: string;
    radius?: number;
    center?: { x: number; y: number; z: number };
    axis?: { x: number; y: number; z: number };
    normal?: { x: number; y: number; z: number };
    color?: OCCTColor | null;
}

/**
 * Edge (kenar) topoloji bilgisi
 */
export interface OCCTEdgeInfo {
    id: number;
    curveType: string;
    radius?: number;
    startPoint?: { x: number; y: number; z: number };
    endPoint?: { x: number; y: number; z: number };
    center?: { x: number; y: number; z: number };
    color?: OCCTColor | null;
}

/**
 * Topoloji parse sonucu (basit)
 */
export interface OCCTTopologyResult {
    success: boolean;
    faces: OCCTFaceInfo[];
    edges: OCCTEdgeInfo[];
    shells: any[];
    solids: any[];
    error: string | null;
}

/**
 * Renk istatistikleri (debug için)
 */
export interface OCCTColorStats {
    /** Toplam solid sayısı */
    total: number;
    /** XCAF'dan renk alınan solid sayısı */
    withColor: number;
    /** Varsayılan renk atanan solid sayısı */
    withoutColor: number;
    /** Renk kaynakları (label-Surf, shape-Gen, vb.) */
    colorSources: { [key: string]: number };
}

/**
 * Renkli STEP parse sonucu (CAF/XDE ile)
 */
export interface OCCTColoredResult {
    success: boolean;
    meshes: OCCTMeshData[];
    faces: OCCTFaceInfo[];
    edges: OCCTEdgeInfo[];
    colors: OCCTColor[];
    colorStats: OCCTColorStats;
    assembly: any | null;
    warning?: string;
    error: string | null;
}

/**
 * OpenCascade.js Worker Bridge
 */
export class FikoOpenCascadeBridge {
    private worker: Worker | null = null;
    private pendingRequests: Map<string, { resolve: Function; reject: Function }> = new Map();
    private requestId = 0;
    private initialized = false;
    private hasCAFSupport = false;
    private initPromise: Promise<void> | null = null;
    private logs: string[] = [];

    /** Progress callback */
    public onProgress?: (message: string, progress: number) => void;

    /** Log callback - worker loglarını dinlemek için */
    public onLog?: (message: string) => void;

    constructor() {
        // Constructor'da worker başlatılmaz, lazy initialization
    }

    /**
     * Worker'ı başlat ve OpenCascade.js'i yükle
     */
    public async init(): Promise<void> {
        if (this.initialized) return;
        if (this.initPromise) return this.initPromise;

        this.initPromise = this._initWorker();
        return this.initPromise;
    }

    private async _initWorker(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                // Worker URL'ini belirle (cache busting ile)
                const workerUrl = `/opencascade/opencascade-worker.js?v=${Date.now()}`;


                this.worker = new Worker(workerUrl);

                this.worker.onmessage = (e) => {
                    const { type, id, success, result, error, message, hasCAFSupport, progress } = e.data;

                    // Log mesajları
                    if (type === 'log') {
                        this.logs.push(message);
                        if (this.onLog) {
                            this.onLog(message);
                        }
                        return;
                    }

                    // Progress mesajları - PERFORMANS: UI güncellemesi için
                    if (type === 'progress') {
                        if (this.onProgress) {
                            this.onProgress(message, progress);
                        }
                        return;
                    }

                    // Init sonucu
                    if (type === 'init-result') {
                        if (success) {
                            this.initialized = true;
                            this.hasCAFSupport = hasCAFSupport || false;
                            resolve();
                        } else {
                            reject(new Error('OpenCascade.js başlatılamadı'));
                        }
                        return;
                    }

                    // Parse sonuçları
                    if (type === 'parse-result' || type === 'error') {
                        const pending = this.pendingRequests.get(id);
                        if (pending) {
                            this.pendingRequests.delete(id);
                            if (type === 'error') {
                                pending.reject(new Error(error));
                            } else {
                                pending.resolve(result);
                            }
                        }
                        return;
                    }
                };

                this.worker.onerror = (e) => {
                    reject(new Error('Worker hatası: ' + e.message));
                };

                // Init mesajı gönder
                this.worker.postMessage({ type: 'init' });

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * CAF/XDE renk desteği var mı?
     */
    public hasColorSupport(): boolean {
        return this.hasCAFSupport;
    }

    /**
     * Initialized durumu
     */
    public isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Worker loglarını al
     */
    public getLogs(): string[] {
        return [...this.logs];
    }

    /**
     * STEP dosyasını parse et (basit - renk yok)
     */
    public async parseSTEP(stepContent: string | ArrayBuffer): Promise<OCCTTopologyResult> {
        await this.init();

        return new Promise((resolve, reject) => {
            const id = `req_${++this.requestId}`;

            this.pendingRequests.set(id, { resolve, reject });

            this.worker!.postMessage({
                type: 'parse-step',
                id,
                data: { stepContent }
            });
        });
    }

    /**
     * STEP dosyasını renklerle birlikte parse et (CAF/XDE)
     * Mesh verileri, renkler ve topoloji bilgisi döndürür
     */
    public async parseSTEPWithColors(
        stepContent: string | ArrayBuffer,
        tessellationParams: TessellationParams = {}
    ): Promise<OCCTColoredResult> {
        await this.init();

        if (!this.hasCAFSupport) {
            const basicResult = await this.parseSTEP(stepContent);
            return {
                success: basicResult.success,
                meshes: [],
                faces: basicResult.faces,
                edges: basicResult.edges,
                colors: [],
                colorStats: {
                    total: 0,
                    withColor: 0,
                    withoutColor: 0,
                    colorSources: {}
                },
                assembly: null,
                warning: 'CAF desteği yok, renk bilgisi alınamadı',
                error: basicResult.error
            };
        }

        return new Promise((resolve, reject) => {
            const id = `req_${++this.requestId}`;

            this.pendingRequests.set(id, { resolve, reject });

            this.worker!.postMessage({
                type: 'parse-step-with-colors',
                id,
                data: {
                    stepContent,
                    tessellationParams: {
                        linearDeflection: tessellationParams.linearDeflection ?? 0.1,
                        angularDeflection: tessellationParams.angularDeflection ?? 0.5,  // 30 derece
                        relativeMode: tessellationParams.relativeMode ?? false,
                        readParams: tessellationParams.readParams ?? {
                            surfaceCurveMode: 3,
                            precisionMode: 1,
                            precisionVal: 0.01,
                            maxPrecisionVal: 1.0,
                            productMode: '1',
                            assemblyLevel: 1,
                            inParallel: true,
                            minSize: 0.01,
                            internalVerticesMode: true,
                            controlSurfaceDeflection: true,
                            encodeRegularityAngle: 0.01,
                            stepSchema: 'AP214',
                            stepSequence: '',
                            resourceName: '',
                            nonManifold: 0,
                            ideasMode: 0,
                            allShapes: true,
                            unit: 'MM'
                        }
                    }
                }
            });
        });
    }

    /**
     * Worker'ı temizle
     */
    public dispose(): void {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        this.pendingRequests.clear();
        this.initialized = false;
        this.initPromise = null;
        this.logs = [];
    }
}

// Singleton instance
let bridgeInstance: FikoOpenCascadeBridge | null = null;

/**
 * FikoOpenCascadeBridge singleton instance'ını al
 */
export function getOpenCascadeBridge(): FikoOpenCascadeBridge {
    if (!bridgeInstance) {
        bridgeInstance = new FikoOpenCascadeBridge();
    }
    return bridgeInstance;
}

/**
 * Bridge instance'ını temizle
 */
export function disposeOpenCascadeBridge(): void {
    if (bridgeInstance) {
        bridgeInstance.dispose();
        bridgeInstance = null;
    }
}
